<template>
  <GiveBadgeDialog
    mode="appoint"
    employeeSelectable
    :value="value"
    :description="description"
    :employees="employees"
    :employeesLoading="employeesLoading"
    :employeesHasMore="employeesHasMore"
    :currentBadgeLevel="currentBadgeLevel"
    @employee-search="onSearch"
    @employee-load-more="loadEmployees"
    @employee-change="onEmployeeChange"
    @appoint-badge="onAppoint"
    @input="onInput"
  />
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onBeforeUnmount } from "vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import { buildAppointBadgePayload, getBadgeLevelNumber } from "@components/utils/appointedBadge";
import { getApiErrorDetail } from "@components/utils/apiErrorDetail";
import { createProficiencyBadge, getEmployeeBadges, getListofEmployeeV1 } from "@/api";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;
const LEVEL_NAMES = { 1: "apprentice", 2: "professional", 3: "coach", 4: "master" };

const props = defineProps({
  value: { type: Boolean, default: false },
  accountId: { type: [Number, String], default: null },
  responsibilityId: { type: [Number, String], default: null },
  responsibilityName: { type: String, default: "" },
});

const emit = defineEmits(["input", "appointed"]);
const { proxy } = getCurrentInstance();

const employees = ref([]);
const employeesLoading = ref(false);
const employeesHasMore = ref(true);
const page = ref(1);
const search = ref("");
const selectedEmployeeBadgeLevel = ref(null);
let searchTimer = null;

const description = computed(() =>
  props.responsibilityName ? `Appoint a badge for ${props.responsibilityName} to an employee.` : ""
);
const currentBadgeLevel = computed(() => selectedEmployeeBadgeLevel.value);

async function loadEmployees() {
  if (employeesLoading.value || !employeesHasMore.value) return;
  const query = search.value;
  try {
    employeesLoading.value = true;
    const response = await getListofEmployeeV1(
      props.accountId,
      PAGE_SIZE,
      (page.value - 1) * PAGE_SIZE,
      query,
      "active"
    );
    if (query !== search.value) return;
    const results = response?.data?.results;
    if (!results) {
      employeesHasMore.value = false;
      return;
    }
    employees.value = [
      ...employees.value,
      ...results.map((el) => ({
        id: el.id,
        access: el.access,
        fullName: `${el.first_name} ${el.last_name}`,
        firstName: el.first_name,
        lastName: el.last_name,
        title: el.primary_positions?.length > 0 ? el.primary_positions[0].name : "",
        avatar: el.avatar,
      })),
    ];
    page.value += 1;
    employeesHasMore.value = employees.value.length < (response.data.count || 0);
  } catch (e) {
    console.error("Error loading employees:", e);
    employeesHasMore.value = false;
  } finally {
    if (query === search.value) employeesLoading.value = false;
  }
}

async function resetEmployees(query) {
  employees.value = [];
  page.value = 1;
  employeesHasMore.value = true;
  search.value = query;
  employeesLoading.value = false;
  await loadEmployees();
}

function onSearch(query) {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => resetEmployees(query || ""), SEARCH_DEBOUNCE_MS);
}

async function onEmployeeChange(employee) {
  selectedEmployeeBadgeLevel.value = null;
  if (!employee?.id || !props.responsibilityId) return;
  try {
    const res = await getEmployeeBadges(props.accountId, employee.id, {
      status: "current",
      responsibility_id: props.responsibilityId,
    });
    const list = res?.data?.results ?? res?.data ?? [];
    const highest = (Array.isArray(list) ? list : []).reduce((max, badge) => {
      const raw = badge?.level ?? badge?.currentBadge?.level;
      return Math.max(max, getBadgeLevelNumber(Number(raw) || raw));
    }, 0);
    selectedEmployeeBadgeLevel.value = LEVEL_NAMES[highest] || null;
  } catch (e) {
    console.error("Error loading the employee's current badges:", e);
  }
}

async function onAppoint({ level, notifyEmployee, employeeId }) {
  emit("input", false);
  try {
    proxy.$loader?.setAppLoading(true);
    await createProficiencyBadge(
      props.accountId,
      buildAppointBadgePayload({
        employeeId,
        responsibilityId: props.responsibilityId,
        level,
        notify: notifyEmployee,
      })
    );
    proxy.$notifications?.success("Badge appointed successfully");
    emit("appointed");
  } catch (e) {
    console.error("Error appointing badge:", e);
    proxy.$notifications?.error(getApiErrorDetail(e) || "Error appointing badge");
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

function onInput(visible) {
  emit("input", visible);
}

watch(
  () => props.value,
  (open) => {
    if (open) {
      selectedEmployeeBadgeLevel.value = null;
      resetEmployees("");
    } else {
      clearTimeout(searchTimer);
    }
  }
);

onBeforeUnmount(() => clearTimeout(searchTimer));
</script>
